import { useEffect, useRef } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useUser } from "util/UserContext"
import {
    consumePendingDestination,
    sanitizeDestination,
} from "util/authDestination"

/**
 * Set of paths where "logging in with no stored destination" should
 * still redirect away from the auth chrome. These are the routes that
 * the viewer could only be on *because* they were signing in; once
 * auth completes the page itself becomes meaningless.
 */
const AUTH_SURFACE_PATHS: ReadonlyArray<string> = [
    "/signin",
    "/signup",
    "/passwordrecovery",
    "/password",
]

/**
 * Hook mounted once at the top of the authenticated shell.
 *
 * Watches `currentUser` for the signed-out → signed-in transition and,
 * when it fires, consumes any pending destination (set by
 * `RequireAuth` or `AuthLogin`) and navigates the viewer there. When
 * no destination is pending and the viewer is stranded on an auth-only
 * surface, falls back to `/`.
 *
 * This centralizes post-auth navigation so every auth entry point
 * (in-page auth wall, standalone `/signin` page, OAuth popup, OAuth
 * redirect) produces the same Reddit-style "land on your intended
 * destination" behavior.
 */
export function usePostAuthRedirect(): void {
    const { currentUser } = useUser()
    const navigate = useNavigate()
    const location = useLocation()

    // Track whether the viewer was authenticated on the previous
    // render so we only fire on the transition, never on steady state.
    // We initialize to whatever the current auth state is so the hook
    // does not misfire on the first client render of a signed-in
    // viewer.
    const wasAuthedRef = useRef<boolean>(Boolean(currentUser))

    useEffect(() => {
        const isAuthed = Boolean(currentUser)
        const wasAuthed = wasAuthedRef.current
        wasAuthedRef.current = isAuthed

        if (!isAuthed || wasAuthed) {
            return
        }

        // Transition: signed-out → signed-in.
        const pendingDest = consumePendingDestination()
        const safeDest = sanitizeDestination(pendingDest)

        if (safeDest) {
            // Stay on the page if it already matches the destination.
            // Avoids a superfluous history entry and router no-op.
            const currentHref = `${location.pathname}${location.search}${location.hash}`
            if (safeDest !== currentHref) {
                navigate(safeDest, { replace: true })
            }
            return
        }

        // Fall back only when the viewer is stranded on an auth-chrome
        // route. Every other route is a legitimate destination the
        // viewer should keep after signing in.
        if (AUTH_SURFACE_PATHS.includes(location.pathname)) {
            navigate("/", { replace: true })
        }
    }, [currentUser, location.pathname, location.search, location.hash, navigate])
}
